<template>
  <div class="dropdown-selected-value" :class="{ 'dropdown-selected-value--disabled': disabled }">
    <template v-if="(isMoreThanTwoSelected && !disabled) || (persistentPlaceholder && selectedValues.length)">
      <span class="dropdown-selected-value__tag" @click="emit('clear')">
        {{ selectedValues.length }}
        <UIIcon name="small-close" size="xs"></UIIcon>
      </span>
      <span v-if="!persistentPlaceholder" @click="emit('focus')">Options Selected</span>
    </template>
    <span v-else-if="getSelectedName()" class="dropdown-selected-value__name" @click="emit('focus')">
      {{ getSelectedName() }}
    </span>
  </div>
</template>
<script setup lang="ts">
import isObject from 'lodash/isObject'
import isArray from 'lodash/isArray'

const props = withDefaults(
  defineProps<{
    items?: any[]
    value?: string | number | number[]
    multiple: boolean
    persistentPlaceholder: boolean
    valueKey: string
    labelKey: string
    disabled?: boolean
  }>(),
  {
    items: () => [],
    value: () => [],
    persistentPlaceholder: false,
  },
)

const emit = defineEmits<{
  (e: 'focus'): void
  (e: 'clear'): void
}>()

const isMoreThanTwoSelected = computed(() => {
  return props.multiple && isArray(props.value) && props.value.length > 2
})

const selectedValue = computed(() => {
  return props.items.find(item => item[props.valueKey] === props.value || item[props.valueKey] === Number(props.value))
})

const selectedValues = computed(() => {
  if (!isArray(props.value)) {
    return []
  }

  return props.value.map((item: any) => props.items.find(i => i[props.valueKey] === item)).filter(Boolean)
})

const getSelectedName = () => {
  if (isArray(props.value)) {
    return selectedValues.value.map((item: any) => item[props.labelKey]).join('; ')
  }

  if (isObject(selectedValue.value)) {
    interface SelectedValue {
      [key: string]: any
    }

    return (selectedValue.value as SelectedValue)[props.labelKey]
  }

  return ''
}
</script>

<style scoped lang="scss">
@import '@/styles/variables.scss';

.dropdown-selected-value {
  display: flex;
  align-items: center;
  white-space: nowrap;
  color: $black-100;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;

  @media screen and (max-width: 1023px) {
    max-width: 180px;
  }

  &:not(:empty) {
    margin-right: 4px;
  }

  &__name {
    overflow: hidden;
    text-overflow: ellipsis;

    @media screen and (max-width: 1023px) {
      font-size: 16px;
    }
  }

  &__tag {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: $gold-primary-100;
    font-size: 12px;
    font-weight: 500;
    min-width: 38px;
    height: 24px;
    padding: 0 6px;
    margin-right: 8px;
    cursor: pointer;
    transition: $base-transition width;

    .icon {
      margin-top: 1px;
    }
  }

  &--disabled {
    color: $black-60;
    cursor: default;
    pointer-events: none;
  }
}
</style>
